import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { humanise, inr, num } from '../lib/format';
import { Button, Card, CardHead, Empty, Field, Input, Notice } from '../components/ui';

interface GstReport {
  from: string;
  to: string;
  byPurpose: Array<{ purpose: string; count: number; basePaise: number; gstPaise: number; grossPaise: number }>;
  totals: { basePaise: number; gstPaise: number; grossPaise: number };
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

export function GstPage() {
  const [from, setFrom] = useState(iso(new Date(Date.now() - 30 * 86_400_000)));
  const [to, setTo] = useState(iso(new Date()));

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['gst', from, to],
    queryFn: () => api.get<GstReport>(`/admin/reports/gst${qs({ from, to })}`),
  });

  function downloadCsv() {
    if (!data) return;
    const rows = [
      ['Purpose', 'Count', 'Base (INR)', 'GST (INR)', 'Gross (INR)'],
      ...data.byPurpose.map((r) => [
        humanise(r.purpose),
        String(r.count),
        (r.basePaise / 100).toFixed(2),
        (r.gstPaise / 100).toFixed(2),
        (r.grossPaise / 100).toFixed(2),
      ]),
      [
        'Total',
        String(data.byPurpose.reduce((n, r) => n + r.count, 0)),
        (data.totals.basePaise / 100).toFixed(2),
        (data.totals.gstPaise / 100).toFixed(2),
        (data.totals.grossPaise / 100).toFixed(2),
      ],
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `gst-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="stack">
      <Notice tone="info" title="Figures come from what was charged">
        Each payment stores its own GST split at the moment of capture, so changing the rate later never rewrites what a
        past invoice says.
      </Notice>

      <div className="filters">
        <Field label="From">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="To">
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <div style={{ alignSelf: 'flex-end' }}>
          <Button variant="primary" disabled={!data} onClick={downloadCsv}>
            Download CSV
          </Button>
        </div>
      </div>

      <Card flush>
        <CardHead title="GST collected" sub={`${from} to ${to}`} />
        {isLoading ? (
          <div style={{ padding: 'var(--s5)' }}>
            <div className="skeleton" style={{ height: 120 }} />
          </div>
        ) : isError ? (
          <Empty icon="⚠️" title="Could not load the report" body={(error as Error).message} />
        ) : !data?.byPurpose.length ? (
          <Empty icon="⎙" title="No captured payments in this period" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Purpose</th>
                  <th style={{ textAlign: 'right' }}>Payments</th>
                  <th style={{ textAlign: 'right' }}>Base</th>
                  <th style={{ textAlign: 'right' }}>GST</th>
                  <th style={{ textAlign: 'right' }}>Gross</th>
                </tr>
              </thead>
              <tbody>
                {data.byPurpose.map((r) => (
                  <tr key={r.purpose}>
                    <td className="cell-strong">{humanise(r.purpose)}</td>
                    <td className="cell-num">{num(r.count)}</td>
                    <td className="cell-num">{inr(r.basePaise, { paise: true })}</td>
                    <td className="cell-num">{inr(r.gstPaise, { paise: true })}</td>
                    <td className="cell-num">{inr(r.grossPaise, { paise: true })}</td>
                  </tr>
                ))}
                <tr style={{ background: 'var(--bg)' }}>
                  <td className="cell-strong">Total</td>
                  <td className="cell-num cell-strong">{num(data.byPurpose.reduce((n, r) => n + r.count, 0))}</td>
                  <td className="cell-num cell-strong">{inr(data.totals.basePaise, { paise: true })}</td>
                  <td className="cell-num cell-strong">{inr(data.totals.gstPaise, { paise: true })}</td>
                  <td className="cell-num cell-strong">{inr(data.totals.grossPaise, { paise: true })}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
